import React, { useRef, useEffect, useState } from "react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { ChatBotPopup, MinimizeSvg } from "@components"
import { StaticImage } from "gatsby-plugin-image"

interface Message {
  id: string
  role: "user" | "assistant"
  content: string
  timestamp: Date
}

export const ChatBot = () => {
  const [isMinimized, setIsMinimized] = useState(true)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const [input, setInput] = useState("")
  const [messages, setMessages] = useState<Message[]>([])
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isClient, setIsClient] = useState(false)

  // Fix hydration by ensuring client-side rendering for timestamps
  useEffect(() => {
    setIsClient(true)
  }, [])

  // Load messages from session storage on component mount
  useEffect(() => {
    if (isClient) {
      const savedMessages = sessionStorage.getItem("chatbot-messages")
      if (savedMessages) {
        try {
          const parsedMessages = JSON.parse(savedMessages).map((msg: any) => ({
            ...msg,
            timestamp: new Date(msg.timestamp),
          }))
          setMessages(parsedMessages)
        } catch (error) {
          console.error("Error loading messages from session storage:", error)
        }
      }
    }
  }, [isClient])

  // Save messages to session storage whenever messages change
  useEffect(() => {
    if (isClient && messages.length > 0) {
      sessionStorage.setItem("chatbot-messages", JSON.stringify(messages))
    }
  }, [messages, isClient])

  const frequentQuestions = [
    "I would like to get a real time quote",
    "I would like to book a free consultancy",
    "I would like to request a custom quote",
    "I would like to see the daily feed",
    "I would like to read client reviews",
  ]

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Handle escape key to exit fullscreen
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isFullscreen) {
        setIsFullscreen(false)
      }
    }

    if (isFullscreen) {
      document.addEventListener("keydown", handleEscape)
      return () => document.removeEventListener("keydown", handleEscape)
    }
  }, [isFullscreen])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }) as never
  }

  const sendMessage = async (text: string) => {
    if (!text.trim() || isLoading) return

    const userMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content: text,
      timestamp: new Date(),
    }

    setMessages((prev) => [...prev, userMessage])
    setIsLoading(true)
    setError(null)

    try {
      const response = await fetch(
        "https://visualfolio-ai.vercel.app/api/chat2",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            messages: [
              ...messages.map((m) => ({
                role: m.role,
                parts: [{ type: "text", text: m.content }],
              })),
              {
                role: "user",
                parts: [{ type: "text", text }],
              },
            ],
          }),
        }
      )

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const reader = response.body?.getReader()
      if (!reader) {
        throw new Error("No response body")
      }

      let assistantContent = ""
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: "",
        timestamp: new Date(),
      }

      setMessages((prev) => [...prev, assistantMessage])

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const chunk = new TextDecoder().decode(value)
        const lines = chunk.split("\n")

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6)
            if (data === "[DONE]") continue

            try {
              const parsed = JSON.parse(data)

              // Handle the AI SDK streaming format
              if (parsed.type === "text-delta" && parsed.delta) {
                assistantContent += parsed.delta
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === assistantMessage.id
                      ? { ...msg, content: assistantContent }
                      : msg
                  )
                )
              }
              // Also handle the old format for backward compatibility
              else if (parsed.type === "text" && parsed.text) {
                assistantContent += parsed.text
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === assistantMessage.id
                      ? { ...msg, content: assistantContent }
                      : msg
                  )
                )
              }
            } catch (e) {
              // Skip invalid JSON lines
              continue
            }
          }
        }
      }
    } catch (err: any) {
      console.error("Chat error:", err)
      setError(err.message || "Failed to send message")
    } finally {
      setIsLoading(false)
    }
  }

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized)
    setIsFullscreen(false)
  }
  const toggleFullscreen = () => setIsFullscreen(!isFullscreen)

  const clearChatHistory = () => {
    setMessages([])
    setError(null)
    if (isClient) {
      sessionStorage.removeItem("chatbot-messages")
    }
  }

  if (isMinimized && !isFullscreen) {
    return (
      <div
        className="fixed bottom-5 right-6 bg-purple-700 text-white rounded-s-xl px-4 py-4 flex items-center gap-2.5 cursor-pointer shadow-lg z-50 hover:scale-110 hover:animate-none transition-transform duration-200 ease-in-out [animation:bounce_2s_infinite]"
        onClick={toggleMinimize}
      >
        <div className="animate-fade-out absolute w-full -top-10 -left-24 right-0">
          <ChatBotPopup />
        </div>
        {/* <Chatbot /> */}
        {/* <Image src={mate} alt="mate" width={30} height={20} /> */}
        <StaticImage
          src="../../images/vf-white.png"
          placeholder="blurred"
          className="w-12"
          alt="Velo"
        />
      </div>
    )
  }

  return (
    <div
      className={`fixed ${
        isFullscreen
          ? "inset-0 w-full h-full"
          : "bottom-5 right-5 lg:w-96 xl:w-96 md:w-96 sm:w-96 w-80 h-[90vh] rounded-xl"
      }  shadow-xl overflow-hidden flex flex-col bg-white z-50`}
    >
      {/* Header */}
      <div className="bg-purple-700 text-white p-4 flex justify-between items-center">
        <div className="">
          <StaticImage
            src="../../images/vf-white.png"
            placeholder="blurred"
            className="w-12"
            alt="Velo"
          />
        </div>
        <h3 className="m-0 text-xl">Velo</h3>
        <div className="flex items-center gap-2">
          {messages.length > 0 && (
            <button
              onClick={clearChatHistory}
              className="bg-transparent border-none text-white text-sm cursor-pointer p-1 hover:bg-purple-600 rounded"
              title="Clear chat history"
            >
              🗑️
            </button>
          )}
          <button
            onClick={toggleFullscreen}
            className="bg-transparent border-none text-white text-xl cursor-pointer p-1 w-6 h-6 flex items-center justify-center hover:bg-purple-600 rounded"
            title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          >
            {isFullscreen ? <MinimizeSvg /> : "⤢"}
          </button>
          <button
            onClick={toggleMinimize}
            className="bg-transparent border-none text-white text-xl cursor-pointer p-0 w-6 h-6 flex items-center justify-center hover:bg-purple-600 rounded"
            title="Minimize Velo"
          >
            −
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 p-4 overflow-y-auto bg-gray-50 flex flex-col gap-3">
        {/* Welcome message - only show when no messages */}
        {/* {messages.length === 0 && ( */}
        <div className="flex justify-start">
          <div className="flex items-end gap-2 max-w-[80%]">
            <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center text-base shrink-0">
              🤖
            </div>
            <div className="bg-white text-gray-800 p-2.5 px-3.5 rounded-2xl shadow-sm break-words">
              <div className="mb-1">
                Hello! I'm Velo, your AI assistant. How can I help you today?
              </div>
              <span className="text-xs opacity-70 block text-right">
                {isClient
                  ? new Date().toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : "--:--"}
              </span>
            </div>
          </div>
        </div>
        {/* )} */}

        {/* Chat messages */}
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${
              message.role === "user" ? "justify-end" : "justify-start"
            }`}
          >
            <div className="flex items-end gap-2 max-w-[90%]">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-base shrink-0 ${
                  message.role === "user" ? "bg-purple-700" : "bg-green-500"
                }`}
              >
                {message.role === "user" ? "👤" : "🤖"}
              </div>
              <div
                className={`p-2.5 px-3.5 rounded-2xl shadow-sm break-words ${
                  message.role === "user"
                    ? "bg-purple-700 text-white"
                    : "bg-white text-gray-800"
                }`}
              >
                <div className="mb-1">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {message.content}
                  </ReactMarkdown>
                </div>
                <span className="text-xs opacity-70 block text-right">
                  {isClient
                    ? message.timestamp.toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : "--:--"}
                </span>
              </div>
            </div>
          </div>
        ))}

        {/* Error message */}
        {error && (
          <div className="flex justify-start">
            <div className="flex items-end gap-2 max-w-[80%]">
              <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center text-base shrink-0">
                ⚠️
              </div>
              <div className="bg-red-50 text-red-800 p-2.5 px-3.5 rounded-2xl shadow-sm border border-red-200">
                <div className="mb-1">
                  {error.includes("overloaded") || error.includes("503")
                    ? "The AI model is currently overloaded. Please try again in a few moments."
                    : "Sorry, I encountered an error. Please try again."}
                </div>
                <span className="text-xs opacity-70 block text-right">
                  {isClient
                    ? new Date().toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : "--:--"}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Typing indicator */}
        {messages.length === 0 && isLoading && (
          <div className="flex justify-start">
            <div className="flex items-end gap-2 max-w-[80%]">
              <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center text-base shrink-0">
                🤖
              </div>
              <div className="bg-white text-gray-800 p-2.5 px-3.5 rounded-2xl shadow-sm">
                <div className="flex py-1">
                  <span className="h-2 w-2 bg-purple-700 rounded-full inline-block mx-0.5 animate-bounce"></span>
                  <span className="h-2 w-2 bg-purple-700 rounded-full inline-block mx-0.5 animate-bounce delay-150"></span>
                  <span className="h-2 w-2 bg-purple-700 rounded-full inline-block mx-0.5 animate-bounce delay-300"></span>
                </div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Frequent Qs */}
      {/* {messages.length === 0 && ( */}
      <div className="flex flex-wrap gap-2 p-3 border-t border-gray-100 bg-gray-50">
        {frequentQuestions.map((q) => (
          <button
            key={q}
            onClick={() => sendMessage(q)}
            className="px-3 py-1.5 text-sm bg-purple-100 hover:bg-purple-200 text-purple-800 rounded-full transition"
          >
            {q}
          </button>
        ))}
      </div>
      {/* )} */}

      {/* Input + Upload */}
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (input.trim()) {
            sendMessage(input)
            setInput("")
          }
        }}
        className="flex p-4 border-t border-gray-200 bg-white items-center gap-2"
      >
        {/* Text input */}
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.currentTarget.value)}
          placeholder="Ask me anything..."
          disabled={isLoading}
          className="flex-1 px-4 py-3 border text-[#000] border-gray-300 rounded-full outline-none text-sm"
        />

        {/* Send button */}
        <button
          disabled={isLoading || input.length < 1}
          className="px-4 py-2 bg-purple-700 text-white border-none disabled:opacity-50 rounded-full cursor-pointer font-bold text-sm"
        >
          Send
        </button>
      </form>
    </div>
  )
}
